import { Fragment, useEffect, useMemo, useState } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Dialog, Transition } from '@headlessui/react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { GET_PRODUCTS, GET_CATEGORY } from '../graphql/product-queries'
import { ASSIGN_CATEGORY_TO_PRODUCT } from '../graphql/product-mutations'
import { Product } from '../types/product'

interface AddProductsModalProps {
  open: boolean
  onClose: () => void
  categoryId: number
  currentProductIds: Set<number>
}

const DEBOUNCE_MS = 200

const AddProductsModal = ({
  open,
  onClose,
  categoryId,
  currentProductIds,
}: AddProductsModalProps) => {
  const [query, setQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [submitting, setSubmitting] = useState(false)

  const [runSearch, { data, loading }] = useLazyQuery(GET_PRODUCTS, {
    fetchPolicy: 'cache-and-network',
  })

  const [assignCategory] = useMutation(ASSIGN_CATEGORY_TO_PRODUCT, {
    refetchQueries: [{ query: GET_CATEGORY, variables: { id: categoryId } }],
    awaitRefetchQueries: true,
  })

  useEffect(() => {
    if (!open) {
      setQuery('')
      setSelectedIds(new Set())
      setSubmitting(false)
      return
    }
    const t = setTimeout(() => {
      runSearch({
        variables: {
          filters: {
            active: true,
            name: query.trim() || undefined,
          },
        },
      })
    }, DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [open, query, runSearch])

  const candidates = useMemo<Product[]>(() => {
    const all: Product[] = data?.products ?? []
    return all.filter((p) => !currentProductIds.has(p.id))
  }, [data, currentProductIds])

  const toggle = (productId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(productId)) next.delete(productId)
      else next.add(productId)
      return next
    })
  }

  const handleConfirm = async () => {
    if (selectedIds.size === 0) return
    setSubmitting(true)
    const ids = Array.from(selectedIds)
    const results = await Promise.allSettled(
      ids.map((productId) =>
        assignCategory({ variables: { productId, categoryId } }),
      ),
    )
    const failures = results.filter((r) => r.status === 'rejected')
    if (failures.length === 0) {
      toast.success(
        ids.length === 1
          ? 'Product added to category'
          : `${ids.length} products added to category`,
      )
      onClose()
    } else if (failures.length === ids.length) {
      const first = failures[0] as PromiseRejectedResult
      toast.error(first.reason?.message || 'Failed to add products')
      setSubmitting(false)
    } else {
      toast.error(
        `Added ${ids.length - failures.length} of ${ids.length} products; ${failures.length} failed`,
      )
      onClose()
    }
  }

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={submitting ? () => {} : onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-lg bg-white rounded-lg shadow-xl flex flex-col max-h-[80vh]">
              <div className="p-6 pb-4 border-b border-gray-200">
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  Add products to category
                </Dialog.Title>
                <p className="mt-1 text-sm text-gray-600">
                  Search active products and pick the ones to assign.
                </p>
                <div className="relative mt-4">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search products…"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    autoFocus
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-2">
                {loading && candidates.length === 0 && (
                  <div className="py-6 text-sm text-gray-500 text-center">Searching…</div>
                )}
                {!loading && candidates.length === 0 && (
                  <div className="py-6 text-sm text-gray-500 text-center">
                    {query.trim()
                      ? 'No matching products available.'
                      : 'No products available to add.'}
                  </div>
                )}
                <ul className="divide-y divide-gray-100">
                  {candidates.map((p) => {
                    const checked = selectedIds.has(p.id)
                    return (
                      <li key={p.id}>
                        <label className="flex items-center gap-3 py-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(p.id)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {p.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {p.kind} · {p.variants?.length ?? 0} variant
                              {(p.variants?.length ?? 0) === 1 ? '' : 's'}
                            </div>
                          </div>
                        </label>
                      </li>
                    )
                  })}
                </ul>
              </div>

              <div className="p-4 border-t border-gray-200 flex items-center justify-between gap-3">
                <span className="text-sm text-gray-500">
                  {selectedIds.size} selected
                </span>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className="btn-primary"
                    disabled={submitting || selectedIds.size === 0}
                  >
                    {submitting
                      ? 'Adding…'
                      : selectedIds.size === 0
                      ? 'Add'
                      : `Add ${selectedIds.size}`}
                  </button>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}

export default AddProductsModal
